import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Editor } from '@tinymce/tinymce-react';
import { useAdminContext } from '../../context/AdminContext';
import './BlogEditForm.css';

const slugify = (str) =>
  str.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');

const BlogEditForm = () => {
  const navigate = useNavigate();
  const { selectedBlog, updateBlog } = useAdminContext();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [imageBase64, setImageBase64] = useState('');
  const [imageLink, setImageLink] = useState('');
  const [author, setAuthor] = useState('');
  const [date, setDate] = useState('');
  const [urlSlug, setUrlSlug] = useState('');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [seoKeywords, setSeoKeywords] = useState('');

  useEffect(() => {
    if (selectedBlog) {
      setTitle(selectedBlog.title || '');
      setContent(selectedBlog.content || '');
      setImageBase64(selectedBlog.imageBase64 || '');
      setImageLink(selectedBlog.imageLink || '');
      setAuthor(selectedBlog.author || '');
      setDate(selectedBlog.date || '');
      setUrlSlug(selectedBlog.urlSlug || '');
      setSeoTitle(selectedBlog.seoTitle || '');
      setSeoDescription(selectedBlog.seoDescription || '');
      setSeoKeywords(selectedBlog.seoKeywords || '');
    }
  }, [selectedBlog]);

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    setUrlSlug(slugify(newTitle));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImageBase64(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title || !content || !author || !date || !urlSlug) {
      toast.error('All fields are required!');
      return;
    }

    try {
      await updateBlog(selectedBlog.id, {
        title,
        content,
        imageBase64,
        imageLink,
        author,
        date,
        urlSlug,
        seoTitle,
        seoDescription,
        seoKeywords,
      });

      toast.success('Blog updated successfully!');
      navigate('/admin/blog');
    } catch (error) {
      toast.error('Error updating blog: ' + error.message);
    }
  };

  return (
    <div className="editblog">
      <div className="editblog-form">
        <button onClick={() => navigate('/admin/blog')} className="back-btn">Back to Blog List</button>
        <h2>Edit Blog</h2>
        <form onSubmit={handleSubmit} className="blog-form">
          <div className="form-group">
            <label htmlFor="title">Title</label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={handleTitleChange}
              placeholder="Enter blog title"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="content">Content</label>
            <Editor
              id="content"
              apiKey="xq724x2m6sc4ecxig8wtrji2ykjkc5xusce71tfozatsdcrd"
              value={content}
              init={{
                height: 400,
                menubar: true,
                plugins: ['advlist autolink lists link image charmap preview anchor',
                          'searchreplace visualblocks code fullscreen',
                          'insertdatetime media table code help wordcount'],
                toolbar: 'undo redo | formatselect | bold italic backcolor | \
                          alignleft aligncenter alignright alignjustify | \
                          bullist numlist outdent indent | removeformat | help',
                branding: false,
              }}
              onEditorChange={(newContent) => setContent(newContent)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="imageLink">Image URL (optional)</label>
            <input
              type="url"
              id="imageLink"
              value={imageLink}
              onChange={(e) => setImageLink(e.target.value)}
              placeholder="Enter image URL"
            />
          </div>

          <div className="form-group">
            <label htmlFor="image">Upload Image</label>
            <input type="file" id="image" onChange={handleImageChange} accept="image/*" />
          </div>

          {(imageBase64 || imageLink) && (
            <img
              src={imageBase64 || imageLink}
              alt="Preview"
              className="image-preview"
            />
          )}

          <div className="form-group">
            <label htmlFor="author">Author</label>
            <input
              type="text"
              id="author"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="Enter author name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="date">Date</label>
            <input
              type="date"
              id="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          <h3>SEO Fields ↓</h3>

          <div className="form-group">
            <label htmlFor="seoTitle">SEO Title</label>
            <input
              type="text"
              id="seoTitle"
              value={seoTitle}
              onChange={(e) => setSeoTitle(e.target.value)}
              placeholder="Enter SEO Title"
            />
          </div>

          <div className="form-group">
            <label htmlFor="seoDescription">SEO Description</label>
            <input
              type="text"
              id="seoDescription"
              value={seoDescription}
              onChange={(e) => setSeoDescription(e.target.value)}
              placeholder="Enter SEO Description"
            />
          </div>

          <div className="form-group">
            <label htmlFor="seoKeywords">SEO Keywords</label>
            <input
              type="text"
              id="seoKeywords"
              value={seoKeywords}
              onChange={(e) => setSeoKeywords(e.target.value)}
              placeholder="recipe, vegan, health"
            />
          </div>

          <button type="submit" className="submit-btn">Update Blog</button>
        </form>
      </div>
    </div>
  );
};

export default BlogEditForm;
